// @ts-ignore
import { CountryCode, ErrorMessage, FieldWrapper } from '@Components/Auth/Auth.styled';
import { CheckBoxWrapper, CheckInput } from '@Components/common/Checkbox';
import { useAuth } from '@Contexts/AuthContext/Auth.context';
import { _GetCities } from '@Services/Addresses';
import { K_CitiesList } from '@Utils/ReactQuery.keys';
import { CountryCode as CountryCodeType } from 'libphonenumber-js/types';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';
import { useRouter } from 'next/router';
import RedCheck from 'public/red-check.svg';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { UnmountClosed } from 'react-collapse';
import { Controller, useForm, UseFormSetError } from 'react-hook-form';
import { getCountryCallingCode, isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useQuery } from 'react-query';
import { AddressFormValues } from 'types/Address';
import {
  AddressFormRow,
  AddressFormWrapper,
  FormGroup,
  FormInput,
  FormSelect,
  StyledCheckLabel,
  StyledPhone,
} from './AddressForm.styled';

type Props = {
  submitHandler: (data: AddressFormValues, setError: UseFormSetError<AddressFormValues>) => void;
  formId: string;
  isLoading: boolean;
};
export default function AddressForm({ submitHandler, formId, isLoading }: Props) {
  const { t } = useTranslation('Checkout');
  const { locale } = useRouter();
  const [moreData, setMoreData] = useState(false);
  const [country, setCountry] = useState<CountryCodeType | undefined>('EG');
  const { userData } = useAuth();
  const { data: citiesData, isLoading: isCitiesLoading } = useQuery(
    K_CitiesList({
      locale,
    }),
    () => _GetCities()
  );
  const {
    register,
    setValue,
    control,
    watch,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<AddressFormValues>();

  const citiesList = useMemo(
    () =>
      citiesData?.results
        .filter((city) => Boolean(city.city_districts.length))
        .map((city) => ({
          value: city.id,
          label: city.name,
        })),
    [citiesData]
  );

  const getDistricts = useCallback(
    (cityId) => {
      return citiesData?.results
        .find((city) => city.id === cityId)
        ?.city_districts.map((district) => ({
          label: district.name,
          value: district.id,
        }));
    },
    [citiesData]
  );

  useEffect(() => {
    const parsePhone = userData?.mobile ? parsePhoneNumber(userData.mobile as string) : null;
    setValue('mobile', parsePhone?.number as string);
    setValue('full_name', userData?.full_name as string);
    setValue('email', userData?.email as string);
    setTimeout(() => {
      setCountry(parsePhone?.country);
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  return (
    <AddressFormWrapper onSubmit={handleSubmit((data) => submitHandler(data, setError))} id={formId}>
      <AddressFormRow>
        <FormGroup>
          <Controller
            name="city_id"
            control={control}
            rules={{
              required: true,
            }}
            render={({ field }) => (
              <FormSelect
                {...field}
                onChange={(value: any) => {
                  setValue('district', null);
                  field.onChange(value);
                }}
                isSearchable={true}
                isClearable={false}
                options={citiesList}
                placeholder={t('select-province')}
                isDisabled={isLoading || isCitiesLoading || !Boolean(citiesData)}
              />
            )}
          />
          {errors?.city_id && (
            <ErrorMessage>{errors.city_id?.type === 'required' && t('select-province')}</ErrorMessage>
          )}
        </FormGroup>
        <FormGroup>
          <Controller
            name="district"
            control={control}
            rules={{
              required: true,
            }}
            render={({ field }) => (
              <FormSelect
                {...field}
                onChange={(value: any) => {
                  field.onChange(value);
                }}
                isSearchable={true}
                isClearable={false}
                options={(watch('city_id') && getDistricts(watch('city_id')?.value)) || undefined}
                placeholder={t('select-district')}
                isDisabled={isLoading || isCitiesLoading || !Boolean(citiesData) || !Boolean(watch('city_id'))}
              />
            )}
          />
          {errors?.district && (
            <ErrorMessage>
              {errors.district?.type === 'required' ? t('select-district') : errors.district?.message}
            </ErrorMessage>
          )}
        </FormGroup>
      </AddressFormRow>
      <AddressFormRow>
        <FormGroup>
          <FormInput
            type="text"
            placeholder={t('enter-street')}
            {...register('street', { required: true })}
            disabled={isLoading}
          />
          {errors?.street && (
            <ErrorMessage>
              {errors.street?.type === 'required' ? t('enter-street') : errors.street?.message}
            </ErrorMessage>
          )}
        </FormGroup>
      </AddressFormRow>
      <AddressFormRow>
        <FormGroup>
          <FormInput
            type="text"
            placeholder={t('enter-building')}
            {...register('building', { required: true })}
            disabled={isLoading}
          />
          {errors?.building && (
            <ErrorMessage>
              {errors.building?.type === 'required' ? t('enter-building') : errors.building?.message}
            </ErrorMessage>
          )}
        </FormGroup>
        <FormGroup>
          <FormInput
            type="text"
            placeholder={t('enter-apartment-number')}
            {...register('apartment_number', { required: true })}
            disabled={isLoading}
          />
          {errors?.apartment_number && (
            <ErrorMessage>
              {errors.apartment_number?.type === 'required'
                ? t('enter-apartment-number')
                : errors.apartment_number?.message}
            </ErrorMessage>
          )}
        </FormGroup>
        <FormGroup>
          <FormInput
            type="text"
            placeholder={t('enter-floor')}
            {...register('floor', { required: true })}
            disabled={isLoading}
          />
          {errors?.floor && (
            <ErrorMessage>{errors.floor?.type === 'required' ? t('enter-floor') : errors.floor?.message}</ErrorMessage>
          )}
        </FormGroup>
      </AddressFormRow>
      <AddressFormRow>
        <CheckBoxWrapper>
          <CheckInput
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMoreData(e.target.checked)}
            name={'address_more_data'}
            id={'address_more_data'}
            type="checkbox"
            checked={moreData}
            disabled={isLoading}
          />
          <StyledCheckLabel htmlFor={'address_more_data'}>
            {t('new_connection')}
            <Image src={RedCheck} width={16} height={12} alt="check" />
          </StyledCheckLabel>
        </CheckBoxWrapper>
      </AddressFormRow>
      <UnmountClosed isOpened={moreData}>
        <AddressFormWrapper as="div">
          <AddressFormRow>
            <FormGroup>
              <FormInput
                type="text"
                placeholder={t('enter-your-name')}
                {...register('full_name', {
                  required: true,
                })}
                disabled={isLoading}
              />
              {errors?.full_name && (
                <ErrorMessage>
                  {errors.full_name?.type === 'required' ? t('enter-your-name') : errors.full_name?.message}
                </ErrorMessage>
              )}
            </FormGroup>
          </AddressFormRow>
          <AddressFormRow>
            <FormInput type="email" placeholder={t('enter-your-email')} {...register('email')} disabled={isLoading} />
            {errors?.email && (
              <ErrorMessage>
                {errors.email?.type === 'required' ? t('enter-email') : errors.email?.message}
              </ErrorMessage>
            )}
          </AddressFormRow>
          <AddressFormRow>
            <FieldWrapper>
              <CountryCode>+{country ? getCountryCallingCode(country) : ''}</CountryCode>
              <Controller
                name="mobile"
                control={control}
                  
                rules={{
                  required: true,
                  validate: (value) => (value ? isValidPhoneNumber(value.toString()) : true),
                }}
                render={({ field: { onChange, value } }) => (
                  <StyledPhone
                    value={value?.toString()}
                    onChange={(v: any) => onChange(v || null)}
                    placeholder={t('enter-phone-number')}
                    defaultCountry={country}
                    smartCaret={false}
                    onCountryChange={(country?: CountryCodeType) => setCountry(country)}
                    disabled={isLoading}
                    id="PHONE"
                  />
                )}
              />
              {Boolean(errors.mobile) && <ErrorMessage>{t('phone_error_validate')}</ErrorMessage>}
            </FieldWrapper>
          </AddressFormRow>
        </AddressFormWrapper>
      </UnmountClosed>
    </AddressFormWrapper>
  );
}
